import "react-datepicker/dist/react-datepicker.css";
import "components/Form/react-datepicker-overrides.css";
import "react-toggle/style.css";
import "styles/overrides.css";
import "@docsearch/css";
import React, { Fragment } from "react";
import { DocSearch } from "@docsearch/react";
import Head from "next/head";
import PropTypes from "prop-types";
import { Provider } from "react-redux";
import { useStore } from "src/reducers";
import { PHONE, DESKTOP, MAX_WIDTH } from "utils/constants/ui";
import { ViewportProvider } from "utils/context";
import { COLORS } from "utils/styles";
import { ToastProvider } from "react-toast-notifications";
import { getBrandTheme, getFontFamilyUrl, isNotGoogleFont } from "utils/theme";
import get from "lodash/get";
import { getConfig } from "utils/env";
import * as Sentry from "@sentry/react";
import { Integrations as TracingIntegrations } from "@sentry/tracing";
import Prism from "prism-react-renderer/prism";
(typeof global !== "undefined" ? global : window).Prism = Prism;
require("prismjs/components/prism-javascript");

Sentry.init({
  enabled: process.env.NODE_ENV === "production",
  dsn: getConfig("SENTRY_DSN"),
  integrations: [
    new TracingIntegrations.BrowserTracing({
      tracingOrigins: [getConfig("API_ROOT")],
      beforeNavigate: (context) => {
        return {
          ...context,
          // You could use your UI's routing library to find the matching
          // route template here. We don't have one right now, so do some basic
          // parameter replacements.
          name: window.location.pathname
            .replace(/\d+/g, "<digits>")
            .replace(/[a-f0-9]{32}/g, "<hash>")
        };
      }
    })
  ],

  // We recommend adjusting this value in production, or using tracesSampler
  // for finer control
  tracesSampleRate: 0.1
});

const App = ({ Component, pageProps }) => {
  const store = useStore(pageProps.initialReduxState);
  const themeInput = get(pageProps, "theme.data", {});
  const brandTheme = getBrandTheme(themeInput);
  const importUrl = !isNotGoogleFont(brandTheme["font-family"])
    ? getFontFamilyUrl({
        family: brandTheme["font-family"]
      })
    : "";
  return (
    <Fragment>
      <Head>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
        />
      </Head>
      <DocSearch
        appId={getConfig("DOCSEARCH_APP_ID")}
        indexName={getConfig("DOCSEARCH_INDEX_NAME")}
        apiKey={getConfig("DOCSEARCH_KEY")}
      />
      <ViewportProvider>
        <ToastProvider placement="bottom-left">
          <Provider store={store}>
            <Component {...pageProps} />
          </Provider>
        </ToastProvider>
      </ViewportProvider>
      <style jsx global>
        {`
        @import url("${importUrl}");
      :global(html),
      :global(body) {
        margin: 0;
        width: 100%;
        height: 100%;
        touch-action: manipulation;
        color: ${brandTheme["color-text--primary"]};
        box-sizing: border-box;
        font-family: ${brandTheme["font-family"]}, sans-serif;
        // Prevent grey overlay on touch
        -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
        -webkit-font-smoothing: antialiased;
      }
      :global(select:hover) {
        background: ${COLORS.whitesmoke};
      }
      :global(select:disabled),
      :global(input:disabled), {
        cursor: not-allowed;
      }
      :global(.cursor-not-allowed) {
        cursor: not-allowed;
      }
      :global(input:focus),
      :global(textarea:focus),
      :global(select:focus),
      :global(.outline-0:focus) {
        border-color: ${COLORS.blueFade};
        border-width: 1px;
        border-style: solid;
        background: #f3f6f9;
      }
      // =========== APP
      :global(.layout) {
        width: 100%;
        color: ${brandTheme["color-text--primary"]};
      }
      :global(code) {
        font-family: monospace;
      }
      :global(.color-text--primary) {
        color: ${brandTheme["color-text--primary"]};
      }
      :global(.color-text--secondary) {
        color: ${brandTheme["color-text--secondary"]};
      }
      :global(.b-primary) {
        border-color: ${brandTheme["color-primary"]};
      }
      :global(.color-primary) {
        color: ${brandTheme["color-primary"]};
      }
      :global(.feature__enabled--checkmark) {
        color: ${brandTheme["bg-primary"]} !important;
      }
      :global(.color-error) {
        color: ${brandTheme["color-error"]};
      }
      :global(.b--error) {
        border-color: ${COLORS.dangerBorder} !important;
      }
      :global(.b--warning) {
        border-color: ${COLORS.warning} !important;
      }
      :global(.bg-primary) {
        background-color: ${brandTheme["bg-primary"]};
      }
      :global(.bg-white--hover:hover) {
        background-color: ${COLORS.whitesmoke};
      }
      :global(.currency-select-min-width) {
        min-width: 5rem;
      }
      :global(.min-w3) {
        min-width: 4rem;
      }
      :global(.min-w3-5) {
        min-width: 7rem;
      }
      @media (max-width: ${PHONE + 1}px) {
        :global(.dn-imp-s) {
          display: none !important;
        }
      }
      :global(.bg-primary--hover) {
        background-color: ${brandTheme["bg-primary--hover"]};
      }
      :global(.bg-primary--fade) {
        background-color: ${brandTheme["bg-primary--fade"]};
      }
      :global(.b--primary) {
        border-color: ${brandTheme["b-primary"]};
      }
      :global(.ba--primary) {
        border-color: ${brandTheme["b-primary"]};
        border-width: 1px;
        border-style: solid;
      }
      :global(.ba--primary--hover:hover) {
        border-color: ${brandTheme["b-primary"]};
        border-width: 1px;
        border-style: solid;
      }
      :global(.editable__decoration--hover:hover) {
        text-decoration: underline !important;
        text-decoration-line: underline !important;
        text-decoration-style: initial !important;
        text-decoration-color: ${brandTheme["color-primary"]};
        text-decoration-thickness: 1px;
      }
      :global(.link__underline:hover) {
        text-decoration: underline !important;
        text-decoration-line: underline !important;
        text-decoration-style: initial !important;
        text-decoration-color: ${brandTheme["color-text--primary"]};
        text-decoration-thickness: 1px;
      }
      :global(p > a) {
        color: ${COLORS.blue};
        font-weight: 500;
        text-decoration: none;
      }
      :global(.link__decoration--hover) {
        color: ${brandTheme["bg-primary--fade"]};
      }

      :global(.link__decoration--hover:hover),
      :global(p > a:hover) {
        color: ${brandTheme["bg-primary"]}
        text-decoration: underline !important;
        text-decoration-line: underline !important;
        text-decoration-style: initial !important;
        text-decoration-color: initial !important;
      }

      :global(.decoration--hover:hover) {
        text-decoration: underline !important;
        text-decoration-line: underline !important;
        text-decoration-style: initial !important;
        text-decoration-color: initial !important;
      }

      :global(button.button--primary),
      :global(a.button--primary) {
        color: ${COLORS.white};
        border-color: ${brandTheme["bg-primary--fade"]};
        background-color: ${brandTheme["bg-primary--fade"]};
      }
      :global(button.button--primary:hover),
      :global(a.button--primary:hover) {
        background-color: ${brandTheme["color-primary"]};
        border-color: ${brandTheme["color-primary"]};
      }
      :global(button.button--primary:focus),
      :global(a.button--primary:focus) {
        border-color: ${brandTheme["b-primary"]};
      }

      :global(button.button--secondary),
      :global(a.button--secondary) {
        color: ${brandTheme["color-primary"]};
        border-color: ${brandTheme["b-primary"]};
        background-color: ${COLORS.white};
      }
      :global(button.button--secondary:hover),
      :global(a.button--secondary:hover) {
        background-color: ${COLORS.whitesmoke};
      }
      :global(button.button--secondary:focus),
      :global(a.button--secondary:focus) {
        border-color: ${brandTheme["b-primary"]};
      }
      
      :global(button.button--primary:disabled),
      :global(a.button--primary:disabled),
      :global(button.button--secondary:disabled),
      :global(a.button--secondary:disabled),
      :global(a.disabled-link-button--primary),
      :global(a.disabled-link-button--secondary) {
        cursor: not-allowed;
        border-color: inherit;
        opacity: 50%;
        color: ${COLORS.disabled};
        background-color: ${COLORS.white};
        border-color: ${COLORS.disabled};
      }

      // =========== BLOCKS
      :global(.BLOCKS__layout) {
        width: 100%;
        color: ${brandTheme["color-text--primary"]};
      } 
      :global(.BLOCKS__color-text--primary) {
        color: ${brandTheme["color-text--primary"]};
      }
      :global(.BLOCKS__color-text--secondary) {
        color: ${brandTheme["color-text--secondary"]};
      }
      :global(.BLOCKS__color-primary) {
        color: ${brandTheme["color-primary"]};
      }
      :global(.BLOCKS__feature__enabled--checkmark) {
        color: ${brandTheme["bg-primary"]};
      }
      :global(.BLOCKS__color-error) {
        color: ${brandTheme["color-error"]};
      }
      :global(.BLOCKS__bg-primary) {
        background-color: ${brandTheme["bg-primary"]};
      }
      :global(.BLOCKS__bg-primary--hover) {
        background-color: ${brandTheme["bg-primary--hover"]};
      }
      :global(.BLOCKS__bg-primary--fade) {
        background-color: ${brandTheme["bg-primary--fade"]};
      }
      :global(.BLOCKS__ba--primary) {
        border-color: ${brandTheme["b-primary"]};
        border-width: 1px;
        border-style: solid;
      }
      :global(.BLOCKS__editable__decoration--hover:hover) {
        text-decoration: underline;
        text-decoration-line: underline;
        text-decoration-style: initial;
        text-decoration-color: ${brandTheme["color-primary"]};
        text-decoration-thickness: 1px;
      }
      :global(.BLOCKS__link__decoration--hover) {
        color: ${brandTheme["color-primary"]};
      }
      :global(.BLOCKS__link__decoration--hover:hover) {
        color: ${brandTheme["color-primary"]};
        text-decoration: underline;
        text-decoration-line: underline;
        text-decoration-style: initial;
        text-decoration-color: initial;
      }
      :global(.BLOCKS__highlight) {
        background-color: ${brandTheme["color-highlight"]};
      }
      
      :global(button.BLOCKS__button--primary),
      :global(a.BLOCKS__button--primary) {
        color: ${COLORS.white};
        border-color: ${brandTheme["bg-primary--fade"]};
        background-color: ${brandTheme["bg-primary--fade"]};
      }
      
      :global(button.BLOCKS__button--primary:hover),
      :global(a.BLOCKS__button--primary:hover) {
        background-color: ${brandTheme["color-primary"]};
        border-color: ${brandTheme["color-primary"]}
      }
      :global(button.BLOCKS__button--primary:focus),
      :global(a.BLOCKS__button--primary:focus) {
        border-color: ${brandTheme["b-primary"]};
      }
      :global(button.BLOCKS__button--primary:disabled),
      :global(a.BLOCKS__button--primary:disabled) {
        cursor: not-allowed;
        border-color: inherit;
      }

      :global(button.BLOCKS__button--secondary),
      :global(a.BLOCKS__button--secondary) {
        color: ${brandTheme["color-primary"]};
        border-color: ${brandTheme["b-primary"]};
        background-color: ${COLORS.white};
      }
      :global(button.BLOCKS__button--secondary:hover),
      :global(a.BLOCKS__button--secondary:hover) {
        background-color: ${COLORS.whitesmoke};
      }
      :global(button.BLOCKS__button--secondary:focus),
      :global(a.BLOCKS__button--secondary:focus) {
        border-color: ${brandTheme["b-primary"]};
      }
      :global(button.BLOCKS__button--secondary:disabled),
      :global(a.BLOCKS__button--secondary:disabled) {
        cursor: not-allowed;
        border-color: inherit;
      }
      :global(button.BLOCKS__button--primary:disabled),
      :global(a.BLOCKS__button--primary:disabled),
      :global(button.BLOCKS__button--secondary:disabled),
      :global(a.BLOCKS__button--secondary:disabled) {
        cursor: not-allowed;
        border-color: inherit;
        opacity: 50%;
        color: ${COLORS.disabled};
        background-color: ${COLORS.white};
        border-color: ${COLORS.disabled};
      }
      // =================================================

      :global(.token-monospace) {
        font-family: monospace;
      }
      :global(.bg-whitesmoke) {
        background-color: ${COLORS.whitesmoke};
      }
      :global(.bg-danger-05) {
        background-color: ${COLORS.danger05};
      }
      :global(.bg-warning-05) {
        color: ${COLORS.warning05Text};
        background-color: ${COLORS.warning05};
      }
      :global(.bg-success-05) {
        color: #007217;
        background-color: ${COLORS.success05};
      }
      :global(.bg-gold-05) {
        color: ${COLORS.gold05Text};
        background-color: ${COLORS.gold05};
      }
      :global(.auth__footerLink--dot::before) {
        content: "•";
        margin: 0px 8px;
        margin: 0px 8px;
        color: ${COLORS.textSecondary};
      }
      :global(select) {
        background: white;
        cursor: pointer;
      }
      :global(input),
      :global(select),
      :global(textarea) {
        outline: none;
      }
      :global(.outline-0) {
        outline: none;
      }
      :global(.brl2--top) {
        border-top-left-radius: .25rem;
      }
      :global(.mw-app) {
        max-width: ${MAX_WIDTH}px;
      }
      :global(.word-break) {
        word-break: break-word;
      }
      :global(.word-break-all) {
        word-break: break-all;
      }
      :global(.cursor-grab) {
        cursor: grab;
      }
      :global(.cursor-auto) {
        cursor: auto;
      }
      :global(.cursor-move) {
        cursor: move;
      }
      :global(.cursor-not-allowed) {
        cursor: not-allowed;
      }
      :global(.pointer-default) {
        cursor: default;
      }
      :global(input:not([type="checkbox"]):not([type="radio"])) {
        -webkit-appearance: none;
      }
      :global(input[type="search"]::-webkit-search-decoration, input[type="search"]::-webkit-search-cancel-button, input[type="search"]::-webkit-search-results-button, input[type="search"]::-webkit-search-results-decoration) {
        -webkit-appearance: none;
      }
      :global(::-webkit-scrollbar) {
        width: 0;
        background: transparent;
      }
      :global(.bg-content--secondary) {
        background-color: ${COLORS.content.secondary};
      }
      :global(.bg-content--hover) {
        background-color: ${COLORS.content.hover};
      }
      :global(.bh--inset) {
        box-shadow: rgba(0, 0, 0, 0.07) -1px 0px inset;
      }
      :global(.bh--inset--hover:hover) {
        box-shadow: rgba(0, 0, 0, 0.07) -1px 0px inset;
      }
      :global(.b-inner--primary) {
        box-shadow: 0px 0px 0px 1px ${brandTheme["color-primary"]} inset;
      }
      :global(.b-inner--primary-fade) {
        box-shadow: 0px 0px 0px 1px ${COLORS.blueFade} inset;
      }
      :global(.sticky) {
        position: sticky;
      }
      :global(.move) {
        cursor: -webkit-move;
        cursor: move
      }
      :global(.user-select-n) {
        user-select: none;
      }
      :global(.hidden) {
        visibility: hidden;
      }
      :global(textarea) {
        -webkit-appearance: none;
      }
      :global(.z-6) {
        z-index: 6;
      }
      :global(.z-7) {
        z-index: 7;
      }
      :global(input:disabled) {
        -webkit-text-fill-color: rgba(51, 51, 51, 0.8);
        -webkit-opacity: 1;
        background: white;
      }
      :global(textarea:disabled) {
        -webkit-text-fill-color: rgba(51, 51, 51, 0.8);
        -webkit-opacity: 1;
        background: white;
      }
      :global(#__next) {
        height: 100%;
      }
      :global(.mw-0) {
        min-width: 0;
      }
      :global(.shadow-0) {
        -webkit-box-shadow: inset 0px 4px 8px -3px rgba(17, 17, 17, 0.06);
        -moz-box-shadow: 0 3px 6px 0 rgba(0, 0, 0, 0.06);
        box-shadow: 0 3px 6px 0 rgba(0, 0, 0, 0.06);
      }
      :global(.hairline-2) {
        border-color: rgba(0, 0, 0, 0.2);
        border-width: 1px;
      }
      :global(.hairline-0) {
        border-color: rgba(0, 0, 0, 0.025);
        border-width: 1px;
      }
      :global(.brn) {
        border-right: none;
      }
      :global(.w2-5) {
        width: 3rem;
      }
      :global(.h5-5) {
        height: 24rem;
      }
      :global(.hairline-1) {
        border-color: rgba(0, 0, 0, 0.08);
        border-width: 1px;
      }
      :global(.hairline-1-dashed-primary) {
          border-color: ${COLORS.blue};
          border-style: dashed;
          border-width: 1px;
        }
      @media (max-width: ${PHONE}px) {
        :global(.bt-hairline-1-s) {
          border-top: 1px solid rgba(0, 0, 0, 0.08)
        }

        :global(.hairline-1-s) {
          border-color: rgba(0, 0, 0, 0.08);
          border-style: solid;
          border-width: 1px;
        }
    
        :global(.shadow-0-s) {
          -webkit-box-shadow: inset 0px 4px 8px -3px rgba(17, 17, 17, 0.06);
          -moz-box-shadow: 0 3px 6px 0 rgba(0, 0, 0, 0.06);
          box-shadow: 0 3px 6px 0 rgba(0, 0, 0, 0.06);
        }
      }
      @media (min-width: ${PHONE + 1}px) {
        :global(.hairline-1-ns) {
          border-color: rgba(0, 0, 0, 0.08);
          border-style: solid;
          border-width: 1px;
        }
    
        :global(.shadow-0-ns) {
          -webkit-box-shadow: inset 0px 4px 8px -3px rgba(17, 17, 17, 0.06);
          -moz-box-shadow: 0 3px 6px 0 rgba(0, 0, 0, 0.06);
          box-shadow: 0 3px 6px 0 rgba(0, 0, 0, 0.06);
        }

        :global(.bg-transparent-ns) {
          background-color: transparent;
        }

        :global(.bln-ns) {
          border-left: 0
        }

        :global(.min-vh-100-ns) {
          min-height:100vh
        }
      }
      @media (min-width: ${DESKTOP}px) {
        :global(.bg-white-l) {
          background-color: ${COLORS.white};
        }
      }
      :global(.b--whitesmoke-hover) {
        border-color: white;
      }
      :global(.b--whitesmoke-hover:hover) {
        border-color: ${COLORS.whitesmoke};
      }
      :global(.ta-man) {
        touch-action: manipulation;
      }
      @media (min-width: ${DESKTOP}px) {
        :global(.f3-5-l) {
          font-size: 30px;
        }
      }
      :global(.flex-shrink-0) {
        flex-shrink: 0;
      }
      :global(.container__float) {
        -webkit-box-shadow: 0 3px 6px 0 rgba(0, 0, 0, 0.08);
        -moz-box-shadow: 0 3px 6px 0 rgba(0, 0, 0, 0.08);
        box-shadow: 0 3px 6px 0 rgba(0, 0, 0, 0.08);
      }
      :global(.b--transparent) {
        border-color: transparent;
      }
      :global(iframe) {
        width: 100%;
      }
      :global(.CalendarDay__selected, .CalendarDay__selected:active, .CalendarDay__selected:hover) {
        background: #4990e2;
        border-width: 1px;
        border-style: solid;
        border-color: #4990e2;
        color: #fff;
      }
      :global(.react-toggle-thumb) {
        box-shadow: none !important;
      }
      :global(.DateInput_input__focused) {
        outline: 0;
        background: #fff;
        border-top: 0;
        border-left: 0;
        border-right: 0;
        bbottom: 2px:
        border-style: solid;
        border-color: #4990e2;
      }
      :global(.bg--black-025) {
        background-color: rgba(0,0,0,0.025)
      }
      :global(.brtl-0) {
        border-top-left-radius:0;
      }
      :global(.brtr-0) {
        border-top-right-radius:0;
      }
      :global(.brbl-0) {
        bbottom-left-radius:0;
      }
      :global(.brbr-0) {
        bbottom-right-radius:0;
      }
      :global(.brtl-2) {
        border-top-left-radius:0.25rem;
      }
      :global(.brtr-2) {
        border-top-right-radius:0.25rem;
      }
      :global(.brbl-2) {
        bbottom-left-radius:0.25rem;
      }
      :global(.brbr-2) {
        bbottom-right-radius:0.25rem;
      }
      :global(.cursor__move:hover) {
        cursor: move;
      }
      :global(.grecaptcha-badge) {
        display: none;
      }
      :global(.app__action--form) {
        max-width: 400px;
      }
      @media (max-width: 30em) {
        :global(.slide__container) {
          height: 16rem;
        }
      }
      @media (min-width: 30em) and (max-width: 60em) {
        :global(.slide__container) {
          height: 16rem;
        }
      }
      @media (min-width: 60em) {
        :global(.slide__container) {
          height: 19rem;
        }
      }

      @media (max-width: 30em) {
        :global(.useCase__container) {
          height: 2.25rem;
        }
      }
      @media (min-width: 30em) and (max-width: 60em) {
        :global(.useCase__container) {
          height: 2.25rem;
        }
      }
      @media (min-width: 60em) {
        :global(.useCase__container) {
          height: 3.125rem;
        }
      }

      @media (max-width: 30em) {
        :global(.service__container) {
          height: 2.25rem;
        }
      }
      @media (min-width: 30em) and (max-width: 60em) {
        :global(.service__container) {
          height: 2.125rem;
        }
      }
      @media (min-width: 60em) {
        :global(.service__container) {
          height: 2.125rem;
        }
      }

      :global(.paymentMethod__container) {
        height: 2rem;
        width: 3.75rem;
      }

      @media (max-width: 30em) {
        :global(.socialService__container) {
          height: 2.25rem;
        }
      }
      @media (min-width: 30em) and (max-width: 60em) {
        :global(.socialService__container) {
          height: 3rem;
        }
      }
      @media (min-width: 60em) {
        :global(.socialService__container) {
          height: 3rem;
        }
      }

      :global(.slide__image::after),
      :global(.image__cover--after::after) {
        content: "";
        position: absolute;
        pointer-events: none;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: transparent;
        background-size: contain;
        background-position: bottom;
        background-repeat: no-repeat;
      }
      :global(path.recharts-tooltip-cursor) {
        fill: whitesmoke;
      }
      `}
      </style>
      <style jsx global>
        {`
          @keyframes placeHolderShimmer {
            0% {
              background-position: -468px 0;
            }
            100% {
              background-position: 468px 0;
            }
          }
          .shimmer {
            color: #eeeeef;
            animation-duration: 2s;
            animation-fill-mode: forwards;
            animation-iteration-count: infinite;
            animation-name: placeHolderShimmer;
            animation-timing-function: linear;
            background: #f6f7f8;
            background: linear-gradient(
              90deg,
              #d5d5d5 10%,
              #f5f5f5 50%,
              #d5d5d5 100%
            );
            background-size: 800px 104px;
            position: relative;
          }
        `}
      </style>
    </Fragment>
  );
};

App.propTypes = {
  Component: PropTypes.any,
  pageProps: PropTypes.shape({
    manifest: PropTypes.shape({
      id: PropTypes.string,
      uuid: PropTypes.string,
      version: PropTypes.string,
      data: PropTypes.shape({
        config: PropTypes.shape({
          pricing: PropTypes.object,
          features: PropTypes.object
        }),
        layout: PropTypes.array,
        entities: PropTypes.array,
        content: PropTypes.object,
        admin: PropTypes.object,
        data: PropTypes.object
      })
    }),
    theme: PropTypes.shape({
      id: PropTypes.string,
      uuid: PropTypes.string,
      version: PropTypes.string,
      data: PropTypes.shape({
        font: PropTypes.shape({
          family: PropTypes.string
        }),
        text: PropTypes.shape({
          primary: PropTypes.string,
          secondary: PropTypes.string
        }),
        color: PropTypes.shape({
          primary: PropTypes.string,
          secondary: PropTypes.string,
          danger: PropTypes.string
        })
      })
    }),
    config: PropTypes.shape({
      route: PropTypes.string,
      subdomain: PropTypes.string
    }),
    initialReduxState: PropTypes.object
  })
};

export default App;
